import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuthStore } from '../../../stores/authStore';
import { REQUIRED_COMPLETION_KEYS } from '../config/settingsConfig';

export type CompletionStatus = 'done' | 'needs_setup' | 'optional' | 'not_applicable';

export interface SettingsCompletion {
  areas: Record<string, CompletionStatus>;
  percentage: number;
  needsAttentionCount: number;
  loading: boolean;
}

export function useSettingsCompletion(): SettingsCompletion {
  const business = useAuthStore(s => s.business);
  const businessId = business?.id;
  const mountedRef = useRef(true);

  const [areas, setAreas] = useState<Record<string, CompletionStatus>>({});
  const [loading, setLoading] = useState(true);

  const compute = useCallback(async () => {
    if (!businessId || !business) {
      setLoading(false);
      return;
    }

    try {
      const [hoursRes, usersRes, bookingRes, servicesRes, invoiceRes] = await Promise.all([
        supabase
          .from('business_hours')
          .select('id')
          .eq('business_id', businessId)
          .limit(1),
        supabase
          .from('users')
          .select('id')
          .eq('business_id', businessId)
          .eq('status', 'active'),
        supabase
          .from('booking_pages')
          .select('slug')
          .eq('business_id', businessId)
          .maybeSingle(),
        supabase
          .from('services')
          .select('id')
          .eq('business_id', businessId)
          .eq('is_active', true)
          .limit(1),
        // logo_url is in the post-retirement keep-set per
        // §SESSION_INVOICE_SETTINGS_LEGACY_RETIREMENT §1.13. The shared
        // InvoiceBrandingForm continues to write this column, so this
        // completion check stays load-bearing through and after
        // substrate cleanup.
        supabase
          .from('invoice_settings')
          .select('logo_url')
          .eq('business_id', businessId)
          .maybeSingle(),
      ]);

      if (!mountedRef.current) return;

      const result: Record<string, CompletionStatus> = {};

      // Profile: has name + address + phone
      result.profile = (business.name && business.address && business.phone)
        ? 'done' : 'needs_setup';

      // Hours: at least 1 row
      result.hours = (hoursRes.data && hoursRes.data.length > 0)
        ? 'done' : 'needs_setup';

      // Team: at least 1 non-owner user, OR solo business
      const teamSize = usersRes.data?.length ?? 0;
      result.team = teamSize <= 1 ? 'done' : 'done'; // If users exist, team is set up

      // Booking page: slug set
      result.booking_page = (bookingRes.data?.slug)
        ? 'done' : 'needs_setup';

      // Services: at least 1 active
      result.services = (servicesRes.data && servicesRes.data.length > 0)
        ? 'done' : 'needs_setup';

      // Stripe Connect
      result.stripe_connect = business.stripe_connect_charges_enabled
        ? 'done' : 'needs_setup';

      // Invoice settings: row exists with logo
      result.invoice_settings = invoiceRes.data
        ? 'done' : 'needs_setup';

      // Subscription: always done during trial, done if paid after
      const trialActive = business.trial_ends_at
        ? new Date(business.trial_ends_at) > new Date()
        : false;
      if (trialActive || business.tier === 'business' || business.tier === 'pro') {
        result.subscription = 'done';
      } else {
        result.subscription = 'needs_setup';
      }

      setAreas(result);
    } catch {
      // Fail silently — completion is non-critical
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [businessId, business]);

  useEffect(() => {
    mountedRef.current = true;
    void compute();
    return () => { mountedRef.current = false; };
  }, [compute]);

  const doneCount = REQUIRED_COMPLETION_KEYS.filter(k => areas[k] === 'done').length;
  const percentage = REQUIRED_COMPLETION_KEYS.length > 0
    ? Math.round((doneCount / REQUIRED_COMPLETION_KEYS.length) * 100)
    : 0;
  const needsAttentionCount = REQUIRED_COMPLETION_KEYS.filter(k => areas[k] === 'needs_setup').length;

  return { areas, percentage, needsAttentionCount, loading };
}
